/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * localStorage as GraphQL Database for Local Development
 *
 * This module treats localStorage as a structured database that matches
 * the GraphQL schema exactly. Mock resolvers read from and write to this
 * "database", providing realistic API behavior without complex workarounds.
 *
 * Benefits:
 * - Single source of truth (localStorage)
 * - Schema-compliant data structure
 * - Natural query/mutation flow
 * - No complex cache merging needed
 * - Realistic "database" simulation
 */

import type {
    SmartApplyApplicationState,
    ContactInfoSmartApplyApplicationState,
    ResumeSmartApplyApplicationState,
    ScreenerQuestionsAndAnswersSmartApplyApplicationState,
    JobSeekerProfilePayload,
} from '../../__generated__/graphql-base-types';

// Storage keys for the localStorage "database"
const STORAGE_KEYS = {
    SMART_APPLY_STATES: 'graphql-db:smartApplyStates',
    JOB_SEEKER_PROFILE: 'graphql-db:jobSeekerProfile',
    METADATA: 'graphql-db:metadata',
} as const;

// Database metadata
interface DatabaseMetadata {
    version: string;
    lastUpdated: number;
    autoSaveEnabled: boolean;
}

// Type for the complete localStorage database structure
interface LocalStorageDatabase {
    smartApplyStates: Record<string, SmartApplyApplicationState>;
    jobSeekerProfile: JobSeekerProfilePayload | null;
    metadata: DatabaseMetadata;
}

// Default metadata
const DEFAULT_METADATA: DatabaseMetadata = {
    version: '1.0.0',
    lastUpdated: Date.now(),
    autoSaveEnabled: true,
};

// Default SmartApplyApplicationState structure (matches GraphQL schema)
const createDefaultSmartApplyState = (id: string): SmartApplyApplicationState => ({
    __typename: 'SmartApplyApplicationState',
    id,
    draftId: `draft_${id}`,
    contactInfo: {
        __typename: 'ContactInfoSmartApplyApplicationState',
        firstName: null,
        lastName: null,
        firstNamePronunciation: null,
        lastNamePronunciation: null,
        email: null,
        phoneNumber: null,
        gender: null,
        dateOfBirth: null,
        city: null,
        area: null,
        country: null,
    },
    resume: {
        __typename: 'ResumeSmartApplyApplicationState',
        resumeDetail: {
            __typename: 'ResumeDetailSmartApplyApplicationState',
            origin: 'NO_RESUME',
            files: [],
        },
        tailoredResume: null,
        qualifications: {
            __typename: 'QualificationsSmartApplyApplicationState',
            additionalDocuments: [],
            certifications: [],
            educations: [],
            languageSkills: [],
            skills: [],
            workExperiences: [],
        },
        relevantExperience: {
            __typename: 'RelevantExperienceSmartApplyApplicationState',
            companyName: null,
            jobTitle: null,
        },
    },
    questionsAndAnswers: {
        __typename: 'ScreenerQuestionsAndAnswersSmartApplyApplicationState',
        _id: 'default_questions_id',
        screener: [],
    },
});

// Default JobSeekerProfile structure (matches GraphQL schema)
const createDefaultJobSeekerProfile = (): JobSeekerProfilePayload => ({
    __typename: 'JobSeekerProfilePayload',
    profile: {
        __typename: 'JobSeekerProfile',
        assignmentMap: [],
        fileInformation: [],
        resume: [
            {
                __typename: 'JobSeekerProfileResume',
                id: 'resume_1',
                dateCreated: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                dateModified: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
                assignments: [],
                associations: [],
                awards: [],
                certifications: [],
                customSections: [],
                educations: [],
                employmentEligibilities: [],
                groups: [],
                languageSkills: [],
                languages: [],
                licenses: [],
                links: [],
                military: [],
                moduleAttemptSlugs: [],
                patents: [],
                publications: [],
                skills: [],
                tags: [],
                workExperiences: [],
            },
            {
                __typename: 'JobSeekerProfileResume',
                id: 'resume_2',
                dateCreated: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
                dateModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
                assignments: [],
                associations: [],
                awards: [],
                certifications: [],
                customSections: [],
                educations: [],
                employmentEligibilities: [],
                groups: [],
                languageSkills: [],
                languages: [],
                licenses: [],
                links: [],
                military: [],
                moduleAttemptSlugs: [],
                patents: [],
                publications: [],
                skills: [],
                tags: [],
                workExperiences: [],
            },
            {
                __typename: 'JobSeekerProfileResume',
                id: 'resume_3',
                dateCreated: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
                dateModified: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
                assignments: [],
                associations: [],
                awards: [],
                certifications: [],
                customSections: [],
                educations: [],
                employmentEligibilities: [],
                groups: [],
                languageSkills: [],
                languages: [],
                licenses: [],
                links: [],
                military: [],
                moduleAttemptSlugs: [],
                patents: [],
                publications: [],
                skills: [],
                tags: [],
                workExperiences: [],
            },
        ],
        defaultInfo: {
            __typename: 'JobSeekerProfileDefaultInfo',
            contactInformation: {
                __typename: 'JobSeekerProfileContactInformation',
                firstName: 'Juwei',
                lastName: 'Huang',
                phoneNumber: '+81 70 1234 5678',
                location: {
                    __typename: 'JobSeekerProfileLocationType',
                    postalCode: '100-0001',
                    address: '1-1-1 Marunouchi',
                    address2: null,
                    city: 'Tokyo',
                    country: 'Japan',
                    unknownLocation: null,
                    formattedLocation: 'Tokyo, Japan',
                },
            },
        },
    },
});

/**
 * localStorage Database Manager
 */
export class LocalStorageGraphQLDatabase {
    private isEnabled: boolean;

    constructor() {
        this.isEnabled = process.env.NODE_ENV === 'development' && typeof window !== 'undefined';

        if (this.isEnabled) {
            this.initializeDatabase();
            this.exposeDevTools();
        }
    }

    /**
     * Initialize database with default data if empty
     */
    private initializeDatabase(): void {
        try {
            // Check if database exists
            if (!localStorage.getItem(STORAGE_KEYS.METADATA)) {
                console.log('🗄️ Initializing localStorage GraphQL database...');

                const initialDatabase: LocalStorageDatabase = {
                    smartApplyStates: {},
                    jobSeekerProfile: createDefaultJobSeekerProfile(),
                    metadata: DEFAULT_METADATA,
                };

                this.saveDatabase(initialDatabase);
                console.log('✅ localStorage GraphQL database initialized');
            } else {
                console.log('🗄️ localStorage GraphQL database loaded');
            }
        } catch (error) {
            console.warn('Failed to initialize localStorage database:', error);
        }
    }

    /**
     * Read the complete database from localStorage
     */
    private readDatabase(): LocalStorageDatabase {
        if (!this.isEnabled) {
            return {
                smartApplyStates: {},
                jobSeekerProfile: createDefaultJobSeekerProfile(),
                metadata: DEFAULT_METADATA,
            };
        }

        try {
            const smartApplyStates = JSON.parse(
                localStorage.getItem(STORAGE_KEYS.SMART_APPLY_STATES) || '{}',
            );
            const jobSeekerProfile =
                JSON.parse(localStorage.getItem(STORAGE_KEYS.JOB_SEEKER_PROFILE) || 'null') ||
                createDefaultJobSeekerProfile();
            const metadata =
                JSON.parse(localStorage.getItem(STORAGE_KEYS.METADATA) || 'null') ||
                DEFAULT_METADATA;

            return {
                smartApplyStates,
                jobSeekerProfile,
                metadata,
            };
        } catch (error) {
            console.warn('Failed to read database, using defaults:', error);
            return {
                smartApplyStates: {},
                jobSeekerProfile: createDefaultJobSeekerProfile(),
                metadata: DEFAULT_METADATA,
            };
        }
    }

    /**
     * Save the complete database to localStorage
     */
    private saveDatabase(database: LocalStorageDatabase): void {
        if (!this.isEnabled) return;

        try {
            database.metadata.lastUpdated = Date.now();

            localStorage.setItem(
                STORAGE_KEYS.SMART_APPLY_STATES,
                JSON.stringify(database.smartApplyStates),
            );
            localStorage.setItem(
                STORAGE_KEYS.JOB_SEEKER_PROFILE,
                JSON.stringify(database.jobSeekerProfile),
            );
            localStorage.setItem(STORAGE_KEYS.METADATA, JSON.stringify(database.metadata));

            console.log('💾 GraphQL database saved to localStorage');
        } catch (error) {
            console.warn('Failed to save database:', error);
        }
    }

    /**
     * QUERY: Get SmartApplyApplicationState by ID
     */
    public getSmartApplyState(id: string): SmartApplyApplicationState | null {
        const database = this.readDatabase();

        if (database.smartApplyStates[id]) {
            console.log(`📖 Query: SmartApplyApplicationState(${id}) found in database`);
            return database.smartApplyStates[id];
        }

        console.log(`📖 Query: SmartApplyApplicationState(${id}) not found, returning null`);
        return null;
    }

    /**
     * QUERY: Get SmartApplyApplicationState by ID or create default
     */
    public getOrCreateSmartApplyState(id: string): SmartApplyApplicationState {
        const existing = this.getSmartApplyState(id);

        if (existing) {
            return existing;
        }

        // Create new state with some default data for better UX
        const newState = createDefaultSmartApplyState(id);

        // Add some default Japanese data for demo purposes
        newState.contactInfo = {
            ...newState.contactInfo,
            firstName: '太郎',
            lastName: '田中',
            firstNamePronunciation: 'タロウ',
            lastNamePronunciation: 'タナカ',
        } as ContactInfoSmartApplyApplicationState;

        this.updateSmartApplyState(id, newState);
        console.log(`📖 Query: Created new SmartApplyApplicationState(${id}) with defaults`);

        return newState;
    }

    /**
     * MUTATION: Update SmartApplyApplicationState
     */
    public updateSmartApplyState(
        id: string,
        updates: Partial<SmartApplyApplicationState>,
    ): SmartApplyApplicationState {
        const database = this.readDatabase();

        // Get existing state or create new one
        const existingState = database.smartApplyStates[id] || createDefaultSmartApplyState(id);

        // Deep merge updates with existing state
        const updatedState: SmartApplyApplicationState = {
            ...existingState,
            ...updates,
            id, // Ensure ID stays consistent
            // Deep merge contactInfo if provided
            contactInfo: updates.contactInfo
                ? { ...existingState.contactInfo, ...updates.contactInfo }
                : existingState.contactInfo,
            // Deep merge resume if provided
            resume: updates.resume
                ? { ...existingState.resume, ...updates.resume }
                : existingState.resume,
            // Deep merge questionsAndAnswers if provided
            questionsAndAnswers: updates.questionsAndAnswers
                ? { ...existingState.questionsAndAnswers, ...updates.questionsAndAnswers }
                : existingState.questionsAndAnswers,
        };

        // Save to database
        database.smartApplyStates[id] = updatedState;
        this.saveDatabase(database);

        console.log(`💾 Mutation: Updated SmartApplyApplicationState(${id})`, updatedState);
        return updatedState;
    }

    /**
     * MUTATION: Update only contactInfo fields
     */
    public updateContactInfo(
        id: string,
        contactInfoUpdates: Partial<ContactInfoSmartApplyApplicationState>,
    ): SmartApplyApplicationState {
        console.log(`🗄️ LocalStorage Database: updateContactInfo called for ${id}`);
        console.log(`🗄️ LocalStorage Database: contactInfoUpdates:`, contactInfoUpdates);

        const result = this.updateSmartApplyState(id, {
            contactInfo: {
                __typename: 'ContactInfoSmartApplyApplicationState',
                ...contactInfoUpdates,
            },
        });

        console.log(`🗄️ LocalStorage Database: updateContactInfo result:`, result);
        console.log(`🗄️ LocalStorage Database: result.contactInfo:`, result?.contactInfo);

        return result;
    }

    /**
     * MUTATION: Update only resume fields
     */
    public updateResume(
        id: string,
        resumeUpdates: Partial<ResumeSmartApplyApplicationState>,
    ): SmartApplyApplicationState {
        return this.updateSmartApplyState(id, {
            resume: {
                __typename: 'ResumeSmartApplyApplicationState',
                ...resumeUpdates,
            } as ResumeSmartApplyApplicationState,
        });
    }

    /**
     * MUTATION: Update only questionsAndAnswers fields
     */
    public updateQuestionsAndAnswers(
        id: string,
        questionsUpdates: Partial<ScreenerQuestionsAndAnswersSmartApplyApplicationState>,
    ): SmartApplyApplicationState {
        return this.updateSmartApplyState(id, {
            questionsAndAnswers: {
                __typename: 'ScreenerQuestionsAndAnswersSmartApplyApplicationState',
                ...questionsUpdates,
            } as ScreenerQuestionsAndAnswersSmartApplyApplicationState,
        });
    }

    /**
     * QUERY: Get JobSeekerProfile
     */
    public getJobSeekerProfile(): JobSeekerProfilePayload {
        const database = this.readDatabase();
        console.log('📖 Query: JobSeekerProfile found in database');
        return database.jobSeekerProfile || createDefaultJobSeekerProfile();
    }

    /**
     * MUTATION: Update JobSeekerProfile
     */
    public updateJobSeekerProfile(
        updates: Partial<JobSeekerProfilePayload>,
    ): JobSeekerProfilePayload {
        const database = this.readDatabase();

        const existingProfile = database.jobSeekerProfile || createDefaultJobSeekerProfile();
        const updatedProfile: JobSeekerProfilePayload = {
            ...existingProfile,
            ...updates,
            // Deep merge profile.defaultInfo.contactInformation if provided
            profile:
                updates.profile && existingProfile.profile
                    ? {
                          ...existingProfile.profile,
                          ...updates.profile,
                          defaultInfo:
                              updates.profile.defaultInfo && existingProfile.profile.defaultInfo
                                  ? {
                                        ...existingProfile.profile.defaultInfo,
                                        ...updates.profile.defaultInfo,
                                        contactInformation:
                                            updates.profile.defaultInfo.contactInformation &&
                                            existingProfile.profile.defaultInfo.contactInformation
                                                ? {
                                                      ...existingProfile.profile.defaultInfo
                                                          .contactInformation,
                                                      ...updates.profile.defaultInfo
                                                          .contactInformation,
                                                      location:
                                                          updates.profile.defaultInfo
                                                              .contactInformation.location &&
                                                          existingProfile.profile.defaultInfo
                                                              .contactInformation.location
                                                              ? {
                                                                    ...existingProfile.profile
                                                                        .defaultInfo
                                                                        .contactInformation
                                                                        .location,
                                                                    ...updates.profile.defaultInfo
                                                                        .contactInformation
                                                                        .location,
                                                                }
                                                              : existingProfile.profile.defaultInfo
                                                                    .contactInformation.location,
                                                  }
                                                : existingProfile.profile.defaultInfo
                                                      .contactInformation,
                                    }
                                  : existingProfile.profile.defaultInfo,
                      }
                    : existingProfile.profile,
        };

        database.jobSeekerProfile = updatedProfile;
        this.saveDatabase(database);

        console.log('💾 Mutation: Updated JobSeekerProfile', updatedProfile);
        return updatedProfile;
    }

    /**
     * Get database statistics
     */
    public getStats(): any {
        const database = this.readDatabase();

        return {
            smartApplyStatesCount: Object.keys(database.smartApplyStates).length,
            hasJobSeekerProfile: !!database.jobSeekerProfile,
            lastUpdated: database.metadata.lastUpdated,
            version: database.metadata.version,
            storageUsed: this.getStorageUsage(),
            sampleStateIds: Object.keys(database.smartApplyStates).slice(0, 3),
        };
    }

    /**
     * Clear all database data
     */
    public clearDatabase(): void {
        if (!this.isEnabled) return;

        try {
            localStorage.removeItem(STORAGE_KEYS.SMART_APPLY_STATES);
            localStorage.removeItem(STORAGE_KEYS.JOB_SEEKER_PROFILE);
            localStorage.removeItem(STORAGE_KEYS.METADATA);

            this.initializeDatabase();
            console.log('🗑️ GraphQL database cleared and reinitialized');
        } catch (error) {
            console.warn('Failed to clear database:', error);
        }
    }

    /**
     * Export database for backup/sharing
     */
    public exportDatabase(): LocalStorageDatabase {
        return this.readDatabase();
    }

    /**
     * Import database from backup
     */
    public importDatabase(database: LocalStorageDatabase): void {
        this.saveDatabase(database);
        console.log('📥 Database imported successfully');
    }

    /**
     * Get localStorage usage statistics
     */
    private getStorageUsage(): any {
        if (!this.isEnabled) return null;

        const getSize = (key: string) => {
            const item = localStorage.getItem(key);
            return item ? item.length : 0;
        };

        return {
            smartApplyStates: getSize(STORAGE_KEYS.SMART_APPLY_STATES),
            jobSeekerProfile: getSize(STORAGE_KEYS.JOB_SEEKER_PROFILE),
            metadata: getSize(STORAGE_KEYS.METADATA),
            total:
                getSize(STORAGE_KEYS.SMART_APPLY_STATES) +
                getSize(STORAGE_KEYS.JOB_SEEKER_PROFILE) +
                getSize(STORAGE_KEYS.METADATA),
        };
    }

    /**
     * Expose development tools globally
     */
    private exposeDevTools(): void {
        (window as any).graphqlDB = {
            // Read operations
            getSmartApplyState: (id: string) => this.getSmartApplyState(id),
            getJobSeekerProfile: () => this.getJobSeekerProfile(),
            getStats: () => this.getStats(),

            // Write operations
            updateContactInfo: (id: string, updates: any) => this.updateContactInfo(id, updates),
            updateResume: (id: string, updates: any) => this.updateResume(id, updates),
            updateJobSeekerProfile: (updates: any) => this.updateJobSeekerProfile(updates),

            // Database management
            clearDatabase: () => this.clearDatabase(),
            exportDatabase: () => this.exportDatabase(),
            importDatabase: (data: any) => this.importDatabase(data),

            // Utilities
            createDefaultState: (id: string) => createDefaultSmartApplyState(id),

            // Quick state inspection
            listStates: () => {
                const db = this.readDatabase();
                return Object.keys(db.smartApplyStates);
            },

            inspectState: (id: string) => {
                const state = this.getSmartApplyState(id);
                console.log(`🔍 SmartApplyApplicationState(${id}):`, state);
                return state;
            },
        };

        console.log('🛠️ GraphQL Database dev tools available:');
        console.log('- window.graphqlDB.getStats() - Database statistics');
        console.log('- window.graphqlDB.listStates() - List all state IDs');
        console.log('- window.graphqlDB.inspectState(id) - Inspect specific state');
        console.log('- window.graphqlDB.clearDatabase() - Reset database');
    }
}

// Export singleton instance
export const graphqlDatabase = new LocalStorageGraphQLDatabase();

// Export convenience functions for mock resolvers
export const querySmartApplyState = (id: string) => graphqlDatabase.getOrCreateSmartApplyState(id);
export const mutateContactInfo = (
    id: string,
    updates: Partial<ContactInfoSmartApplyApplicationState>,
) => graphqlDatabase.updateContactInfo(id, updates);
export const mutateResume = (id: string, updates: Partial<ResumeSmartApplyApplicationState>) =>
    graphqlDatabase.updateResume(id, updates);
export const mutateQuestionsAndAnswers = (
    id: string,
    updates: Partial<ScreenerQuestionsAndAnswersSmartApplyApplicationState>,
) => graphqlDatabase.updateQuestionsAndAnswers(id, updates);
export const queryJobSeekerProfile = () => graphqlDatabase.getJobSeekerProfile();
export const mutateJobSeekerProfile = (updates: Partial<JobSeekerProfilePayload>) =>
    graphqlDatabase.updateJobSeekerProfile(updates);

export default graphqlDatabase;
